"use client";

import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";

interface UseMobileMenuOptions {
  isLoading?: boolean;
  user?: unknown;
  isAdmin?: boolean;
}

// Focusable elements selector - memoized outside the hook to avoid recreation
const FOCUSABLE_ELEMENTS_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

/**
 * Hook to manage mobile menu state, focus trap, and scroll lock
 */
export function useMobileMenu(options: UseMobileMenuOptions = {}) {
  const { isLoading, user, isAdmin } = options;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const pathname = usePathname();
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuContainerRef = useRef<HTMLDivElement>(null);
  const prevMenuOpenRef = useRef(isMenuOpen);
  const { lock: lockBodyScroll, unlock: unlockBodyScroll } = useBodyScrollLock();

  // Track client mount for safe portal usage
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Close menu when navigating to a new page
  useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  // Handle body scroll lock when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }

    return () => {
      unlockBodyScroll();
    };
  }, [isMenuOpen, lockBodyScroll, unlockBodyScroll]);

  // Focus trap for mobile menu
  useEffect(() => {
    if (!isMenuOpen || !menuContainerRef.current) return;

    const menuContainer = menuContainerRef.current;

    // Get all focusable elements within the menu
    const getFocusableElements = () => {
      return Array.from(
        menuContainer.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENTS_SELECTOR)
      ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);
    };

    const focusableElements = getFocusableElements();

    // Add document-level Escape handler for better accessibility
    // Register this even when no focusable elements exist (e.g., during loading)
    const handleDocumentEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Check if menu container still exists (menu is open)
        const container = menuContainerRef.current;
        if (container) {
          e.preventDefault();
          e.stopPropagation();
          setIsMenuOpen(false);
        }
      }
    };

    // Register Escape handler
    document.addEventListener("keydown", handleDocumentEscape, true); // Use capture phase

    // If no focusable elements, only register Escape handler and return
    if (focusableElements.length === 0) {
      return () => {
        document.removeEventListener("keydown", handleDocumentEscape, true);
      };
    }

    // Focus first element when menu opens
    const firstElement = focusableElements[0];
    firstElement?.focus();

    // Handle Tab key trapping
    const handleNoFocusCase = (e: KeyboardEvent, focusableElements: HTMLElement[]) => {
      e.preventDefault();
      if (e.shiftKey) {
        // Shift + Tab: focus last element
        focusableElements[focusableElements.length - 1]?.focus();
      } else {
        // Tab: focus first element
        focusableElements[0]?.focus();
      }
    };

    const handleShiftTab = (
      e: KeyboardEvent,
      currentIndex: number,
      focusableElements: HTMLElement[]
    ) => {
      // Shift + Tab: move backward
      if (currentIndex <= 0) {
        e.preventDefault();
        focusableElements[focusableElements.length - 1]?.focus();
      }
    };

    const handleTab = (
      e: KeyboardEvent,
      currentIndex: number,
      focusableElements: HTMLElement[]
    ) => {
      // Tab: move forward
      if (currentIndex >= focusableElements.length - 1) {
        e.preventDefault();
        focusableElements[0]?.focus();
      }
    };

    const handleTabNavigation = (e: KeyboardEvent, focusableElements: HTMLElement[]) => {
      const currentIndex = focusableElements.findIndex((el) => el === document.activeElement);

      // Handle case where no element is focused (currentIndex === -1)
      if (currentIndex === -1) {
        handleNoFocusCase(e, focusableElements);
        return;
      }

      if (e.shiftKey) {
        handleShiftTab(e, currentIndex, focusableElements);
      } else {
        handleTab(e, currentIndex, focusableElements);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      handleTabNavigation(e, focusableElements);
    };

    menuContainer.addEventListener("keydown", handleKeyDown);

    return () => {
      menuContainer.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keydown", handleDocumentEscape, true);
    };
  }, [isMenuOpen, isLoading, user, isAdmin]);

  // Return focus to menu button when menu closes
  useEffect(() => {
    const wasOpen = prevMenuOpenRef.current;
    const isNowClosed = !isMenuOpen && wasOpen;

    if (isNowClosed && menuButtonRef.current) {
      menuButtonRef.current.focus();
    }

    prevMenuOpenRef.current = isMenuOpen;
  }, [isMenuOpen]);

  return {
    isMenuOpen,
    setIsMenuOpen,
    menuButtonRef,
    menuContainerRef,
    isClient,
  };
}
